import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createFaq, updateFaq, deleteFaq, getFaqs } from '../helper/Api/AdminApi';

const FaqListLayer = () => {
  const [faqs, setFaqs] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [currentFaq, setCurrentFaq] = useState(null);
  const [formData, setFormData] = useState({
    formationCategory: 'Investir',
    question: '',
    answer: '',
    isActive: 'Yes',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 25, 50, 'All'];
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const fetchedFaqs = await getFaqs();
      setFaqs(fetchedFaqs);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch Formation Records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showModal || showConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, showConfirm]);

  const filteredFaqs = faqs.filter(faq => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      faq.formationCategory?.toLowerCase().includes(searchLower) ||
      faq.question?.toLowerCase().includes(searchLower) ||
      faq.answer?.toLowerCase().includes(searchLower) ||
      faq.isActive?.toLowerCase().includes(searchLower)
    );
  });

  const totalItems = filteredFaqs.length;
  const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));

  const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
  const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;

  const paginatedFaqs = pageSize === 'All'
    ? filteredFaqs
    : filteredFaqs.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let start = currentPage - halfVisible;
      let end = currentPage + halfVisible;

      if (start < 1) {
        start = 1;
        end = maxVisiblePages;
      }

      if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxVisiblePages + 1;
      }

      for (let i = start; i <= end; i++) pages.push(i);
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (pageSize === 'All') {
      setCurrentPage(1);
    }
  }, [pageSize]);

  const toggleExpand = (id, field) => {
    setExpandedItems(prev => ({
      ...prev,
      [`${id}-${field}`]: !prev[`${id}-${field}`]
    }));
  };

  const TruncatedText = ({ text, id, field, maxLines = 2 }) => {
    const isExpanded = expandedItems[`${id}-${field}`];

    return (
      <div>
        <div
          className={`text-wrap ${isExpanded ? '' : 'text-truncate-multiline'}`}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.5',
            maxHeight: isExpanded ? 'none' : `${maxLines * 1.5}em`
          }}
        >
          {text}
        </div>
        {text && text.length > 100 && (
          <button
            type="button"
            className="btn btn-link text-primary-600 text-sm p-0 mt-1 text-decoration-none"
            onClick={() => toggleExpand(id, field)}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.formationCategory) {
      newErrors.formationCategory = 'Please select a Formation Category';
    }

    if (!formData.question) {
      newErrors.question = 'Please enter a Question';
    }

    if (!formData.answer) {
      newErrors.answer = 'Please enter the Answer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      try {
        const faqData = {
          Category: formData.formationCategory,
          Question: formData.question,
          Answer: formData.answer,
          isActive: formData.isActive === "Yes" ? true : false,
        };

        if (currentFaq) {
          const response = await updateFaq(currentFaq.id, faqData);

          setFaqs(
            faqs.map((faq) =>
              faq.id === currentFaq.id
                ? {
                  ...faq,
                  formationCategory: formData.formationCategory,
                  question: formData.question,
                  answer: formData.answer,
                  isActive: formData.isActive,
                }
                : faq
            )
          );

          toast.success(response.message || "Formation Record updated successfully!");
        } else {
          const response = await createFaq(faqData);

          setFaqs([
            ...faqs,
            {
              id: response.Data?.ID || faqs.length + 1,
              formationCategory: formData.formationCategory,
              question: formData.question,
              answer: formData.answer,
              isActive: formData.isActive,
            },
          ]);

          toast.success(response.message || "Formation Record created successfully!");
        }

        setShowModal(false);
        setFormData({
          formationCategory: "Investir",
          question: "",
          answer: "",
          isActive: "Yes",
        });
        setCurrentFaq(null);
        setErrors({});
      } catch (error) {
        toast.error(error.response?.data?.message || "Operation failed");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (faq) => {
    setCurrentFaq(faq);
    setFormData({ ...faq });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await deleteFaq(id);
      setFaqs(faqs.filter((faq) => faq.id !== id));
      toast.success(response.message || 'Formation Record deleted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete Formation Record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm text-secondary-light">Show</span>
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={loading}
              >
                {pageSize === 'All' ? 'All' : pageSize}
              </button>
              <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                {pageSizeOptions.map((option) => (
                  <li key={option}>
                    <button
                      className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                      onClick={() => handlePageSizeChange(option)}
                      disabled={loading}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form className="navbar-search">
            <input
              type="text"
              className="form-control radius-8 h-40-px w-auto"
              name="search"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
        </div>

        <button
          className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          onClick={() => {
            setCurrentFaq(null);
            setFormData({ formationCategory: 'Investir', question: '', answer: '', isActive: 'Yes' });
            setShowModal(true);
          }}
          disabled={loading}
        >
          <Icon icon="ic:baseline-plus" className="icon text-lg line-height-1" />
          Add New Formation Record
        </button>
      </div>

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading Faqs...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col" className="text-center">Action</th>
                    <th scope="col">Formation Category</th>
                    <th scope="col">Question</th>
                    <th scope="col">Answer</th>
                    <th scope="col">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFaqs.length > 0 ? (
                    paginatedFaqs.map((faq) => (
                      <tr key={faq.id}>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(faq)}
                              disabled={loading}
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>
                            <button
                              type="button"
                              className="bg-danger-50 text-danger-600 bg-hover-danger-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => {
                                setDeleteId(faq.id);
                                setShowConfirm(true);
                              }}
                              disabled={loading}
                            >
                              <Icon icon="fluent:delete-24-regular" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                        <td>{faq.formationCategory}</td>
                        <td style={{ maxWidth: '300px' }}>
                          <TruncatedText
                            text={faq.question}
                            id={faq.id}
                            field="question"
                            maxLines={2}
                          />
                        </td>
                        <td style={{ maxWidth: '400px' }}>
                          <TruncatedText
                            text={faq.answer}
                            id={faq.id}
                            field="answer"
                            maxLines={2}
                          />
                        </td>
                        <td>{faq.isActive}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        {searchTerm ? 'No results found for your search.' : 'No formation records available.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
              <div className="d-flex align-items-center gap-3">
                <span className="text-secondary-light text-sm">
                  Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                  {Math.min(endIndex, totalItems)} of {totalItems} entries
                </span>
              </div>

              {pageSize !== 'All' && totalPages > 1 && (
                <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-left" className="text-lg" />
                    </button>
                  </li>

                  {getPageNumbers().map((page) => (
                    <li className="page-item" key={page}>
                      <button
                        className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${currentPage === page
                            ? "bg-primary-600 text-white shadow-sm"
                            : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                          }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-right" className="text-lg" />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header p-15 border-bottom">
                <h4 className="modal-title text-xl fw-semibold text-primary-light">{currentFaq ? 'Edit Formation Record' : 'Add New Formation Record'}</h4>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} disabled={loading}></button>
              </div>
              <div className="modal-body p-24">
                <div className="row mb-4">
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Formation Category <span className="text-danger-600">*</span>
                    </label>
                    <div className="dropdown">
                      <button
                        className="form-control radius-8 border border-gray-400 bg-white text-primary-light fw-medium focus:outline-none focus:ring-2 focus:ring-primary-600 text-start d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        disabled={loading}
                        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                      >
                        {formData.formationCategory}
                        <Icon icon="akar-icons:chevron-down" className="ms-2 text-primary-600" />
                      </button>
                      <ul className="dropdown-menu w-100 border border-gray-300 shadow-lg" style={{ borderRadius: '8px' }}>
                        {['Investir', 'Actions', 'Obligations', 'Or'].map((cat, index) => (
                          <li key={cat}>
                            <button
                              className="dropdown-item text-primary-light fw-medium py-2"
                              type="button"
                              onClick={() => setFormData({ ...formData, formationCategory: cat })}
                              disabled={loading}
                              style={{
                                borderBottom: index < 3 ? '1px solid #e5e7eb' : 'none',
                                backgroundColor: 'transparent',
                                transition: 'background-color 0.2s ease',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              {cat}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {errors.formationCategory && <span className="text-danger-600 text-sm mt-1">{errors.formationCategory}</span>}
                  </div>
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Is this Question Active <span className="text-danger-600">*</span>
                    </label>
                    <div className="d-flex align-items-center gap-4 pt-10">
                      <div className="form-check d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="isActive"
                          id="isActiveYes"
                          value="Yes"
                          checked={formData.isActive === 'Yes'}
                          onChange={handleInputChange}
                          disabled={loading}
                        />
                        <label
                          className="form-check-label text-primary-light text-sm ms-2 mb-0"
                          htmlFor="isActiveYes"
                        >
                          Yes
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="isActive"
                          id="isActiveNo"
                          value="No"
                          checked={formData.isActive === 'No'}
                          onChange={handleInputChange}
                          disabled={loading}
                        />
                        <label
                          className="form-check-label text-primary-light text-sm ms-2 mb-0"
                          htmlFor="isActiveNo"
                        >
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-md-12 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Question <span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="question"
                      value={formData.question}
                      onChange={handleInputChange}
                      placeholder="Enter Question"
                      rows="4"
                      disabled={loading}
                    />
                    {errors.question && <span className="text-danger-600 text-sm mt-1">{errors.question}</span>}
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-md-12 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Answer <span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="answer"
                      value={formData.answer}
                      onChange={handleInputChange}
                      placeholder="Enter Answer"
                      rows="6"
                      disabled={loading}
                    />
                    {errors.answer && <span className="text-danger-600 text-sm mt-1">{errors.answer}</span>}
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-3 mt-5">
                  <button
                    type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : currentFaq ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirm && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.2)" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content border-0 radius-12 shadow-lg p-24">
              <h6 className="text-center fw-semibold mb-12">Are you sure?</h6>
              <p className="text-center text-secondary-light mb-20" style={{ fontSize: "14px" }}>
                This action cannot be undone.
              </p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-light px-20 py-6 radius-8"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger px-20 py-6 radius-8"
                  onClick={() => {
                    setShowConfirm(false);
                    handleDelete(deleteId);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqListLayer;